import { useState, useMemo } from "react";
import * as Fmt from "../../format";
import { useScenario } from "../../state";
import { Icons } from "../../components";
import { Model } from "../../model";
import type { CustomBenchmark } from "../../types";

export function Benchmarks() {
  const { active, result } = useScenario();
  const a = active.assumptions;
  const d = result.developer;
  const buildings = active.buildings;
  const [customBMs, setCustomBMs] = useState<CustomBenchmark[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("cls-custom-benchmarks") || "[]");
    } catch {
      return [];
    }
  });
  const [showAdd, setShowAdd] = useState(false);
  const [newBM, setNewBM] = useState({
    metric: "",
    low: "",
    mid: "",
    high: "",
    unit: "%",
  });

  const blendedRent =
    result.campus.totalNlaSqm > 0
      ? d.netRentYear1 / result.campus.totalNlaSqm
      : 0;
  const blendedOutgoings = useMemo(
    () =>
      result.campus.totalNlaSqm > 0
        ? buildings.reduce((s, b) => s + b.nlaSqm * b.outgoingsPerSqm, 0) /
          result.campus.totalNlaSqm
        : 0,
    [buildings, result.campus.totalNlaSqm],
  );
  // All-in construction $/m² (hard net-trade + preliminaries + builder margin) — the like-for-like
  // basis to compare against the data file's whole-building education rates (which are all-in).
  const allInConstrSqm =
    result.campus.totalGfaSqm > 0
      ? (d.hardConstruction + d.preliminaries + d.builderMargin) /
        result.campus.totalGfaSqm
      : 0;
  const rentRevY1 = result.kpis.rentPctRevenueYear1 * 100;

  const rows = [
    {
      key: "rent",
      unit: "$/sqm",
      current: blendedRent,
      metric: "Net rent / sqm (academic)",
    },
    {
      key: "outg",
      unit: "$/sqm",
      current: blendedOutgoings,
      metric: "Outgoings / sqm",
    },
    {
      key: "hard",
      unit: "$/sqm",
      current: allInConstrSqm,
      metric: "Construction / sqm GFA (all-in)",
    },
    {
      key: "cap",
      unit: "%",
      current: a.capRate * 100,
      metric: "Cap rate (school assets)",
    },
    {
      key: "pri",
      unit: "$/student",
      current: a.primaryFeeYear1,
      metric: "Tuition fee — primary",
    },
    {
      key: "sec",
      unit: "$/student",
      current: a.secondaryFeeYear1,
      metric: "Tuition fee — secondary",
    },
    {
      key: "util",
      unit: "%",
      current: a.steadyStateUtilisation * 100,
      metric: "Steady-state utilisation",
    },
    {
      key: "ramp",
      unit: "yrs",
      current: a.rampYears,
      metric: "Ramp to steady state",
    },
    {
      key: "rentRev",
      unit: "%",
      current: rentRevY1,
      metric: "Rent / revenue (Y1)",
    },
    {
      key: "varCost",
      unit: "$/student",
      current: a.variableCostPerStudent,
      metric: "Variable cost / student",
    },
  ];

  // Use the scenario's school-spec tier for the construction benchmark (BENCHMARKS ships the
  // private_mid default; override with the live scenario tier so the comparison is scenario-driven).
  const allBenchmarks = useMemo(() => {
    const scenarioConstrBM = Model.constructionBenchmark(a.schoolSpecTier);
    return [
      ...Model.BENCHMARKS.map((b) =>
        b.metric === "Construction / sqm GFA (all-in)" ? scenarioConstrBM : b,
      ),
      ...customBMs,
    ];
  }, [a.schoolSpecTier, customBMs]);

  const addCustom = () => {
    if (
      !newBM.metric ||
      newBM.low === "" ||
      newBM.mid === "" ||
      newBM.high === ""
    )
      return;
    const bm: CustomBenchmark = {
      metric: newBM.metric,
      low: +newBM.low,
      mid: +newBM.mid,
      high: +newBM.high,
      unit: newBM.unit,
    };
    const next = [...customBMs, bm];
    setCustomBMs(next);
    try {
      localStorage.setItem("cls-custom-benchmarks", JSON.stringify(next));
    } catch {
      /* noop */
    }
    setNewBM({ metric: "", low: "", mid: "", high: "", unit: "%" });
    setShowAdd(false);
  };

  const removeCustom = (idx: number) => {
    const next = customBMs.filter((_, i) => i !== idx);
    setCustomBMs(next);
    try {
      localStorage.setItem("cls-custom-benchmarks", JSON.stringify(next));
    } catch {
      /* noop */
    }
  };

  return (
    <div className="page fade-in">
      <div className="page-strip">
        <div>
          <div className="eyebrow" style={{ marginBottom: 4 }}>
            Market context
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 28,
              fontWeight: 600,
            }}
          >
            Benchmarks
          </h2>
          <div className="lede" style={{ marginTop: 6 }}>
            Your inputs versus market-wide ranges. The dot is the current value;
            the band shows the typical low–mid–high.
          </div>
        </div>
      </div>

      <div className="card elevated card-pad-lg">
        {/* Horizontal scroll at narrow widths — the comparison panel is tabular (fixed columns) */}
        <div
          className="table-scroll"
          tabIndex={0}
          role="region"
          aria-label="Benchmark comparison table"
        >
          <div style={{ minWidth: 640 }}>
            {/* Column header */}
            <div
              className="grid"
              style={{
                gridTemplateColumns: "240px 72px 1fr 72px 140px 90px",
                columnGap: 20,
                alignItems: "baseline",
                paddingBottom: 14,
                marginBottom: 6,
                borderBottom: "1px solid hsl(var(--c-border-subtle))",
              }}
            >
              <div className="eyebrow">Metric</div>
              <div className="eyebrow text-right">Low</div>
              <div className="eyebrow" style={{ textAlign: "center" }}>
                Market range
              </div>
              <div className="eyebrow">High</div>
              <div className="eyebrow text-right">Current</div>
              <div className="eyebrow text-right">Status</div>
            </div>

            <div className="col" style={{ gap: 0 }}>
              {allBenchmarks.map((bm, i) => {
                const isCustom = i >= Model.BENCHMARKS.length;
                const row = rows.find((r) => r.metric === bm.metric);
                const cur = row?.current ?? 0;
                const min = Math.min(bm.low, cur);
                const max = Math.max(bm.high, cur);
                const pad = (max - min) * 0.08 || 1;
                const lo = min - pad,
                  hi = max + pad;
                const xs = (v: number) =>
                  Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
                const inBand = cur >= bm.low && cur <= bm.high;

                const fmt = (v: number) => {
                  if (bm.unit === "%") return Fmt.pct(v / 100, 1);
                  if (bm.unit === "$/sqm") return `${Fmt.num(v, 0)} / sqm`;
                  if (bm.unit === "$/student")
                    return `${Fmt.num(v, 0)} / student`;
                  if (bm.unit === "$") return Fmt.fullCurrency(v);
                  if (bm.unit === "yrs") return `${Fmt.num(v, 1)} yrs`;
                  return String(v);
                };

                const dotColor = inBand
                  ? "hsl(var(--c-success))"
                  : "hsl(var(--c-warning))";
                const statusLabel = inBand
                  ? "In range"
                  : cur < bm.low
                    ? "Below"
                    : "Above";
                const bandGrad = inBand
                  ? "linear-gradient(90deg, hsl(var(--c-success) / 0.10), hsl(var(--c-success) / 0.28), hsl(var(--c-success) / 0.10))"
                  : "linear-gradient(90deg, hsl(var(--c-warning) / 0.10), hsl(var(--c-warning) / 0.25), hsl(var(--c-warning) / 0.10))";
                const bandBorder = inBand
                  ? "hsl(var(--c-success) / 0.35)"
                  : "hsl(var(--c-warning) / 0.35)";

                return (
                  <div
                    key={`${bm.metric}-${i}`}
                    className="grid benchmark-row"
                    style={{
                      gridTemplateColumns: "240px 72px 1fr 72px 140px 90px",
                      columnGap: 20,
                      padding: "14px 0",
                      borderBottom:
                        i === allBenchmarks.length - 1
                          ? "none"
                          : "1px solid hsl(var(--c-border-subtle) / 0.5)",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="benchmark-metric">{bm.metric}</div>
                      <div className="benchmark-source">
                        {"source" in bm
                          ? (bm as { source: string }).source
                          : "Custom"}
                        {isCustom ? (
                          <span
                            className="pill"
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              padding: "1px 6px",
                            }}
                          >
                            Custom
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className="text-right mono p-micro"
                      style={{ color: "hsl(var(--c-foreground-muted))" }}
                    >
                      {fmt(bm.low)}
                    </div>

                    <div
                      style={{
                        position: "relative",
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {/* baseline track */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "50%",
                          height: 6,
                          transform: "translateY(-50%)",
                          background: "hsl(var(--c-card-elevated))",
                          borderRadius: 999,
                        }}
                      ></div>
                      {/* low-high band */}
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          transform: "translateY(-50%)",
                          left: `${xs(bm.low)}%`,
                          width: `${xs(bm.high) - xs(bm.low)}%`,
                          height: 10,
                          background: bandGrad,
                          borderRadius: 999,
                          border: `1px solid ${bandBorder}`,
                        }}
                      ></div>
                      {/* mid tick */}
                      <div
                        style={{
                          position: "absolute",
                          left: `calc(${xs(bm.mid)}% - 1px)`,
                          top: "50%",
                          height: 14,
                          transform: "translateY(-50%)",
                          width: 2,
                          background: "hsl(var(--c-success) / 0.6)",
                          borderRadius: 1,
                        }}
                      ></div>
                      {/* current value dot */}
                      <div
                        title={`Current: ${fmt(cur)}`}
                        style={{
                          position: "absolute",
                          left: `calc(${xs(cur)}% - 8px)`,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 16,
                          height: 16,
                          borderRadius: 999,
                          background: dotColor,
                          border: "2px solid hsl(var(--c-card))",
                          boxShadow: `0 0 14px ${dotColor}`,
                        }}
                      ></div>
                    </div>

                    <div
                      className="mono p-micro"
                      style={{ color: "hsl(var(--c-foreground-muted))" }}
                    >
                      {fmt(bm.high)}
                    </div>

                    <div
                      className="text-right mono"
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: inBand
                          ? "hsl(var(--c-foreground))"
                          : "hsl(var(--c-warning))",
                      }}
                    >
                      {fmt(cur)}
                    </div>

                    <div className="row benchmark-actions">
                      {isCustom ? (
                        <button
                          type="button"
                          className="btn ghost benchmark-delete"
                          onClick={() =>
                            removeCustom(i - Model.BENCHMARKS.length)
                          }
                          title="Remove"
                          aria-label="Remove benchmark"
                        >
                          ×
                        </button>
                      ) : null}
                      <span
                        className={`pill ${inBand ? "success" : "warn"}`}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Custom benchmark adder */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 14,
            borderTop: "1px solid hsl(var(--c-border-subtle) / 0.5)",
          }}
        >
          {!showAdd ? (
            <button
              type="button"
              className="btn ghost"
              onClick={() => setShowAdd(true)}
            >
              <Icons.Plus size={14} stroke={2} /> Add custom benchmark
            </button>
          ) : (
            <div
              className="row gap-2"
              style={{ flexWrap: "wrap", alignItems: "center" }}
            >
              <input
                className="input"
                placeholder="Name"
                aria-label="Benchmark name"
                value={newBM.metric}
                onChange={(e) => setNewBM({ ...newBM, metric: e.target.value })}
                style={{ flex: "2 1 140px", minWidth: 0 }}
              />
              <input
                className="input"
                placeholder="Low"
                aria-label="Low value"
                type="number"
                value={newBM.low}
                onChange={(e) => setNewBM({ ...newBM, low: e.target.value })}
                style={{ flex: "1 1 72px", minWidth: 0 }}
              />
              <input
                className="input"
                placeholder="Mid"
                aria-label="Mid value"
                type="number"
                value={newBM.mid}
                onChange={(e) => setNewBM({ ...newBM, mid: e.target.value })}
                style={{ flex: "1 1 72px", minWidth: 0 }}
              />
              <input
                className="input"
                placeholder="High"
                aria-label="High value"
                type="number"
                value={newBM.high}
                onChange={(e) => setNewBM({ ...newBM, high: e.target.value })}
                style={{ flex: "1 1 72px", minWidth: 0 }}
              />
              <select
                className="select"
                value={newBM.unit}
                onChange={(e) => setNewBM({ ...newBM, unit: e.target.value })}
              >
                <option value="%">%</option>
                <option value="$">$</option>
                <option value="$/sqm">$/sqm</option>
                <option value="$/student">$/student</option>
                <option value="yrs">yrs</option>
              </select>
              <button type="button" className="btn primary" onClick={addCustom}>
                Add
              </button>
              <button
                type="button"
                className="btn ghost"
                onClick={() => setShowAdd(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
